(() => {
  "use strict";

  const qs = (root, selector) => root.querySelector(selector);

  function renameEasifyMaterialLabels() {
    const configs = window.TPOConfigs;
    const optionSets = configs && configs.option_sets_searchable;
    if (!Array.isArray(optionSets)) return;

    optionSets.forEach((optionSet) => {
      const options = optionSet && optionSet.data && optionSet.data.options;
      if (!Array.isArray(options)) return;

      options.forEach((option) => {
        if (!option || option.title !== "Material" || !Array.isArray(option.option_types)) return;

        option.option_types.forEach((optionType) => {
          if (optionType && optionType.label === "Giclee Matte Print") {
            optionType.label = "Giclee Paper";
          }
        });
      });
    });
  }

  function normalize(value) {
    return String(value || "").toLowerCase().replace("luster", "lustre").replace(/[^a-z0-9]+/g, "");
  }

  function optionParts(variant) {
    const parts = [variant && variant.option1, variant && variant.option2, variant && variant.option3].filter(Boolean);
    if (parts.length) return parts.map((part) => String(part).trim());
    return String((variant && variant.title) || "").split("/").map((part) => part.trim()).filter(Boolean);
  }

  function finishKey(label) {
    const value = normalize(label);
    if (value.includes("textured")) return "textured";
    if (value.includes("smooth")) return "smooth";
    if (value.includes("matte")) return "matte";
    if (value.includes("lustre")) return "lustre";
    return value;
  }

  function variantHasFinishData(variants, materialType) {
    const markers = materialType === "fine-art" ? ["smooth", "textured"] : ["lustre", "matte"];
    return (variants || []).some((variant) => optionParts(variant).some((part) => markers.some((marker) => normalize(part).includes(marker))));
  }

  function variantMatchesFinish(variant, finish) {
    const key = finishKey(finish);
    return !key || optionParts(variant).some((part) => normalize(part).includes(key));
  }

  function variantMatchesColour(variant, colour) {
    const key = normalize(colour);
    return optionParts(variant).some((part) => normalize(part) === key);
  }

  function hasVariants(variants) {
    return Array.isArray(variants) && variants.length > 0;
  }

  function normalizeFineArtMaterials(studio) {
    if (!studio || !studio.products) return;

    const fineArtVariants = hasVariants(studio.products.fineArt) ? studio.products.fineArt : studio.products.fineArtTextured;
    const texturedVariants = hasVariants(studio.products.fineArtTextured) ? studio.products.fineArtTextured : fineArtVariants;
    const nextMaterials = [];

    if (hasVariants(studio.products.photo)) {
      nextMaterials.push({
        key: "photo",
        type: "photo",
        label: studio.t ? studio.t("photoLabel", "Photo paper") : "Photo paper",
        note: studio.t ? studio.t("photoNote", "Lustre or matte photo print") : "Lustre or matte photo print",
        variants: studio.products.photo,
      });
    }

    if (hasVariants(fineArtVariants)) {
      nextMaterials.push({
        key: "fine-art",
        type: "fine-art",
        label: "Fine art paper",
        note: "Archival smooth or textured paper",
        variants: fineArtVariants,
        texturedVariants,
      });
    }

    if (hasVariants(studio.products.matteCanvas)) {
      nextMaterials.push({
        key: "matte-canvas",
        type: "canvas",
        floaterKey: "matteFloater",
        label: studio.t ? studio.t("matteCanvasLabel", "Matte canvas") : "Matte canvas",
        note: studio.t ? studio.t("matteCanvasNote", "Low-glare canvas print") : "Low-glare canvas print",
        variants: studio.products.matteCanvas,
      });
    }

    if (hasVariants(studio.products.satinCanvas)) {
      nextMaterials.push({
        key: "satin-canvas",
        type: "canvas",
        floaterKey: "satinFloater",
        label: studio.t ? studio.t("satinCanvasLabel", "Satin canvas") : "Satin canvas",
        note: studio.t ? studio.t("satinCanvasNote", "Subtle sheen and rich colour") : "Subtle sheen and rich colour",
        variants: studio.products.satinCanvas,
      });
    }

    if (!nextMaterials.length) return;

    const selectedKey = studio.material && studio.material.key;
    studio.materials = nextMaterials;
    studio.material = nextMaterials.find((material) => material.key === selectedKey) || nextMaterials[0];
  }

  function patchStudio(Studio) {
    const proto = Studio && Studio.prototype;
    if (!proto || proto.__paperOptionsPatched) return;

    proto.__paperOptionsPatched = true;
    const originalRenderMaterials = proto.renderMaterials;
    const originalRenderPaperFinishes = proto.renderPaperFinishes;
    const originalApplySizeFilterDefaults = proto.applySizeFilterDefaults;
    const originalVariant = proto.variant;
    const originalSourceVariants = proto.sourceVariants;
    const originalPaperFinishOptions = proto.paperFinishOptions;

    proto.paperFinishOptions = function () {
      if (this.material && this.material.type === "photo") {
        return [
          { label: "Lustre", note: "Popular photo paper finish" },
          { label: "Matte", note: "Low-glare photo paper finish" },
        ];
      }
      if (this.material && this.material.type === "fine-art") {
        return [
          { label: "Fine Art Smooth Paper", note: "Clean archival paper surface" },
          { label: "Fine Art Textured Paper", note: "Watercolour-style tooth and texture" },
        ];
      }
      return originalPaperFinishOptions ? originalPaperFinishOptions.call(this) : [];
    };

    proto.defaultPaperFinish = function () {
      const options = this.paperFinishOptions();
      const current = finishKey(this.paperFinish);
      const match = options.find((option) => finishKey(option.label) === current);
      return match ? match.label : options[0] ? options[0].label : "";
    };

    proto.sourceVariants = function () {
      if (this.material && this.material.type === "fine-art" && this.frameMode !== "stock") {
        if (finishKey(this.paperFinish) === "textured" && hasVariants(this.material.texturedVariants)) return this.material.texturedVariants;
        return this.material.variants || [];
      }
      return originalSourceVariants ? originalSourceVariants.call(this) : [];
    };

    proto.applySizeFilterDefaults = function () {
      if (!this.sizeFilterHasProducts || !this.materialCompatible || !this.compatibleMaterials) {
        if (originalApplySizeFilterDefaults) originalApplySizeFilterDefaults.call(this);
        return;
      }
      if (!this.sizeFilterHasProducts(this.sizeFilter)) {
        const first = this.sizeFilterDefinitions().find((filter) => this.sizeFilterHasProducts(filter.key));
        this.sizeFilter = first ? first.key : "print-only";
      }
      if (!this.materialCompatible(this.material)) this.material = this.compatibleMaterials()[0] || this.materials[0];
      this.frameMode = this.defaultFrameMode();
      this.paperFinish = this.defaultPaperFinish();
      const colours = this.frameColours();
      if (colours.length && !colours.includes(this.frameColour)) this.frameColour = colours[0];
    };

    proto.renderMaterials = function () {
      if (!this.materialsEl || !this.compatibleMaterials) {
        if (originalRenderMaterials) originalRenderMaterials.call(this);
        return;
      }
      this.applySizeFilterDefaults();
      this.materialsEl.innerHTML = "";
      this.compatibleMaterials().forEach((material) => {
        const picked = this.material && this.material.key === material.key;
        const label = document.createElement("label");
        label.className = "print-studio__choice" + (picked ? " is-picked" : "");
        label.innerHTML = '<input type="radio" name="studio_material" ' + (picked ? "checked" : "") + "><strong>" + material.label + "</strong><small>" + material.note + "</small>";
        label.addEventListener("click", () => {
          this.material = material;
          this.paperFinish = this.defaultPaperFinish();
          this.frameMode = this.defaultFrameMode();
          this.pick(this.materialsEl, label);
          if (typeof this.renderSizes === "function") this.renderSizes();
          if (typeof this.renderFinishOptions === "function") this.renderFinishOptions();
          if (typeof this.sync === "function") this.sync();
        });
        this.materialsEl.append(label);
      });
    };

    proto.renderPaperFinishes = function () {
      const el = qs(this, "[data-paper-finishes]");
      if (!el) {
        if (originalRenderPaperFinishes) originalRenderPaperFinishes.call(this);
        return;
      }
      el.innerHTML = "";
      const options = this.paperFinishOptions();
      this.paperFinish = this.defaultPaperFinish();
      options.forEach((option) => {
        const picked = finishKey(option.label) === finishKey(this.paperFinish);
        const label = this.modeLabel("studio_paper_finish", option.label, option.note, picked);
        label.addEventListener("click", () => {
          this.paperFinish = option.label;
          this.pick(el, label);
          if (typeof this.sync === "function") this.sync();
        });
        el.append(label);
      });
    };

    proto.variant = function () {
      if (!this.material || !this.size || typeof this.sourceVariants !== "function" || typeof this.sizeOf !== "function") {
        return originalVariant ? originalVariant.call(this) : null;
      }
      const variants = this.sourceVariants();
      const hasFinishVariants = this.showPaperFinishes && this.showPaperFinishes() && variantHasFinishData(variants, this.material.type);
      return variants.find((variant) => {
        const sameSize = normalize(this.sizeOf(variant)) === normalize(this.size);
        const available = variant.available !== false && variant.availableForSale !== false;
        const sameColour = !this.isStockFramePaper || !this.isStockFramePaper() || variantMatchesColour(variant, this.frameColour);
        const sameFinish = !hasFinishVariants || variantMatchesFinish(variant, this.paperFinish);
        return sameSize && sameColour && sameFinish && available;
      }) || null;
    };
  }

  function refreshStudio(studio) {
    normalizeFineArtMaterials(studio);
    if (typeof studio.renderMaterials === "function") studio.renderMaterials();
    if (typeof studio.renderSizes === "function") studio.renderSizes();
    if (typeof studio.renderFinishOptions === "function") studio.renderFinishOptions();
    if (typeof studio.sync === "function") studio.sync();
    polishFineArtDom(studio);
    observeStudio(studio);
  }

  function polishFineArtDom(studio) {
    if (!studio) return;

    const materialLabels = Array.from(studio.querySelectorAll("[data-materials] .print-studio__choice"));
    const smooth = materialLabels.find((label) => /smooth fine art paper/i.test(label.textContent));
    const textured = materialLabels.find((label) => /textured fine art paper/i.test(label.textContent));

    if (smooth) {
      const strong = smooth.querySelector("strong");
      const small = smooth.querySelector("small");
      if (strong && strong.textContent.trim() !== "Fine art paper") strong.textContent = "Fine art paper";
      if (small && small.textContent.trim() !== "Archival smooth or textured paper") small.textContent = "Archival smooth or textured paper";
    }

    if (textured) {
      textured.hidden = true;
      textured.style.display = "none";
    }

    Array.from(studio.querySelectorAll("[data-paper-finishes] .print-studio__mode strong")).forEach((strong) => {
      const text = strong.textContent.trim();
      if (text === "Smooth") strong.textContent = "Fine Art Smooth Paper";
      if (text === "Textured") strong.textContent = "Fine Art Textured Paper";
    });
  }

  function observeStudio(studio) {
    if (!studio || studio.__paperOptionsDomObserved) return;
    studio.__paperOptionsDomObserved = true;
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        polishFineArtDom(studio);
      });
    });
    observer.observe(studio, { childList: true, subtree: true });
  }

  function initDomFallback() {
    document.querySelectorAll("print-frame-studio").forEach((studio) => {
      polishFineArtDom(studio);
      observeStudio(studio);
    });
  }

  function startDomFallbackWatcher() {
    initDomFallback();

    const startedAt = Date.now();
    const interval = setInterval(() => {
      initDomFallback();
      const studio = document.querySelector("print-frame-studio");
      const hasMaterialChoices = studio && studio.querySelector("[data-materials] .print-studio__choice");
      if (hasMaterialChoices || Date.now() - startedAt > 15000) clearInterval(interval);
    }, 300);

    const root = document.body || document.documentElement;
    if (!root || typeof MutationObserver === "undefined") return;

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        initDomFallback();
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    const Studio = customElements.get("print-frame-studio");
    patchStudio(Studio);
    document.querySelectorAll("print-frame-studio").forEach(refreshStudio);
  }

  renameEasifyMaterialLabels();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      startDomFallbackWatcher();
    });
  } else {
    startDomFallbackWatcher();
  }

  if (window.customElements && window.customElements.whenDefined) window.customElements.whenDefined("print-frame-studio").then(init);
})();

(() => {
  "use strict";

  if (window.__905CanvasMediaSwitcherLoader) return;
  window.__905CanvasMediaSwitcherLoader = true;

  const currentScript = document.currentScript;
  if (!currentScript || !currentScript.src) return;

  const mediaSwitcherSrc = currentScript.src.replace(/print-studio-paper-options(?:\.[^/?]+)?\.js(\?.*)?$/, "print-product-media-switcher.js$1");
  if (!mediaSwitcherSrc || mediaSwitcherSrc === currentScript.src) return;

  const script = document.createElement("script");
  script.src = mediaSwitcherSrc;
  script.defer = true;
  document.head.appendChild(script);
})();
